import { useState } from "react";
import { useDispatch } from "react-redux";
import useMerchantPortalAxios from "../../utils/axios";
import {
  setError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";

function normalizeCountRows(rows = []) {
  if (!rows.length) {
    return {
      totalPages: 0,
      totalCount: 0,
    };
  }

  return {
    totalPages: rows[0]?.TotalPages || 0,
    totalCount: rows[0]?.TotalCount || 0,
  };
}

function normalizeBalancePayload(payload = {}) {
  const currenciesOverview = Array.isArray(payload?.CurrenciesOverview)
    ? payload.CurrenciesOverview
    : [];

  const usdItem =
    currenciesOverview.find(
      (item) =>
        item?.Currency === "USD" ||
        item?.CurrencyID === 1 ||
        item?.ID === 1,
    ) || {};
  const lbpItem =
    currenciesOverview.find(
      (item) =>
        item?.Currency === "LBP" ||
        item?.CurrencyID === 2 ||
        item?.ID === 2,
    ) || {};

  const getCurrencyBalances = (item) => {
    const currencyBalances = item?.CurrencyBalances || item || {};

    return {
      available:
        currencyBalances?.Available ??
        item?.AvailableBalance ??
        0,
      held: currencyBalances?.Held ?? 0,
      book: currencyBalances?.Book ?? 0,
    };
  };

  const usdBalances = getCurrencyBalances(usdItem);
  const lbpBalances = getCurrencyBalances(lbpItem);

  return {
    USD: usdBalances.available,
    LBP: lbpBalances.available,
    details: {
      USD: usdBalances,
      LBP: lbpBalances,
    },
  };
}

export default function useNewStatementOfAccountAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const [statements, setStatements] = useState([]);
  const [soaLoading, setSoaLoading] = useState(false);
  const [totalNbOfPage, setTotalNbOfPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);

  async function GetSOAList({
    CurrencyID: currencyId,
    lastDate,
    DateFrom: dateFrom,
    DateTo: dateTo,
    TransactionType: transactionType,
    AccountType: accountType,
    Input: searchInput,
    PageSize: pageSize = 10,
  } = {}) {
    const normalizedLastDate = lastDate || null;
    setSoaLoading(true);
    dispatch(setTableLoading({ key: "transactionHistorySuyool", loading: true }));
    dispatch(setError(null));

    try {
      const response = await client.post(
        "/v1/StatementOfAccount/GetSOAList",
        {
          SessionID: sessionStorage.getItem("SessionID"),
          CurrencyID: currencyId,
          lastDate: normalizedLastDate,
          DateFrom: dateFrom,
          DateTo: dateTo,
          TransactionType: transactionType,
          PageSize: pageSize,
          AccountType: accountType,
          Input: searchInput,
        },
        { timeout: 60000 },
      );

      const rows = response?.data?.Payload?.StatementsOfAccount || [];
      const counts = normalizeCountRows(rows);

      setStatements(rows);
      setTotalNbOfPage(counts.totalPages);
      setTotalCount(counts.totalCount);
      dispatch(
        setTableData({
          key: "transactionHistorySuyool",
          data: rows,
          pagination: {
            lastDate: normalizedLastDate,
            totalPages: counts.totalPages,
            totalCount: counts.totalCount,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setFeatureData({
          section: "forms",
          key: "transactionHistorySuyool",
          value: {
            currencyId,
            transactionType,
            dateFrom,
            dateTo,
          },
        }),
      );
 

      return rows;
    } catch (error) {
      dispatch(
        setTableData({
          key: "transactionHistorySuyool",
          data: [],
          pagination: {
            lastDate: normalizedLastDate,
            totalPages: 0,
            totalCount: 0,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setError(
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Unable to load statement of account.",
        ),
      );
      throw error;
    } finally {
      setSoaLoading(false);
      dispatch(setTableLoading({ key: "transactionHistorySuyool", loading: false }));
    }
  }

  async function GetSOAExport(
    currencyId,
    dateFrom,
    dateTo,
    transactionType,
    accountType,
    searchInput,
  ) {
    setExportLoading(true);

    try {
      const response = await client.post(
        "/v1/StatementOfAccount/GetSOAList",
        {
          SessionID: sessionStorage.getItem("SessionID"),
          CurrencyID: currencyId,
          page: 1,
          pageSize: 1,
          DateFrom: dateFrom,
          DateTo: dateTo,
          TransactionType: transactionType,
          AccountType: accountType,
          Input: searchInput,
          isExport: true,
        },
        { timeout: 60000 },
      );

      return response?.data?.Payload?.StatementsOfAccount || [];
    } finally {
      setExportLoading(false);
    }
  }

  async function GetSuyoolAccountBalance(dateFrom, dateTo) {
    const response = await client.post("/v1/Overview/GetAccountBalance", {
      SessionID: sessionStorage.getItem("SessionID"),
      DateFrom: dateFrom,
      DateTo: dateTo,
    });

    const balances = normalizeBalancePayload(response?.data?.Payload);
    dispatch(
      setFeatureData({
        section: "summary",
        key: "accountSummary",
        value: {
          ...balances,
          dateFrom,
          dateTo,
        },
      }),
    );

    return balances;
  }

  async function DownloadCorporateSoa(payload) {
    const response = await client.post(
      "/v1/StatementOfAccount/DownloadCorporateSoa",
      payload,
    );

    return response?.data?.Payload;
  }

  return {
    GetSOAList,
    GetSOAExport,
    GetSuyoolAccountBalance,
    DownloadCorporateSoa,
    statements,
    soaLoading,
    totalNbOfPage,
    totalCount,
    exportLoading,
  };
}
